/** @format */

import React from 'react';

import {Box, Typography} from '@mui/material';

import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import CancelIcon from '@mui/icons-material/Cancel';
import ScheduleIcon from '@mui/icons-material/Schedule';

import {formatNumber} from 'helpers/formatNumber';
import {ColumnType} from 'components/Table/core/types';

import ProgressBar from '../components/ProgressBar';
import YandexDirectLink from '../components/YandexDirectLink';
import {
  CampaignCellContainer,
  DescriptionHeader,
  FilterIconButton,
  FullDescription,
  KpiAndBudgetCellContainer,
  ToggleDetailsButton,
} from '../styles';

import {ACTION_TYPE_LABELS, ActionType} from '../constants';
import {numericSorting} from './numericSorting';

interface SegmentTableColumnsConfig {
  entityType: string;
  maxKpiFact: number;
  maxForecastKpi: number;
  maxForecastBudget: number;
  kpiName: string;
  kpiUnits: string;
  targetKpi: number;
  targetBudget: number;
  plannedBudget: number;
  onCampaignFilterClick?: (campaignId: string) => void;
  onCampaignNameClick?: (campaignId: string) => void;
  onToggleDetails?: (campaignId: string | null) => void;
}

export const createSegmentTableColumns = (config: SegmentTableColumnsConfig): ColumnType[] => {
  const {
    entityType,
    maxKpiFact,
    maxForecastKpi,
    maxForecastBudget,
    kpiName,
    kpiUnits,
    targetKpi,
    targetBudget,
    plannedBudget,
    onCampaignFilterClick,
    onCampaignNameClick,
    onToggleDetails,
  } = config;

  return [
    {
      id: 'number',
      title: '№ Номер',
      enableFilter: false,
      size: 80,
      sortingFn: numericSorting,
      cell: info => {
        const isSubRow = info.row.depth > 0;

        if (isSubRow) {
          // Получаем данные из хэша
          const data = info.row.original as any;

          return (
            <FullDescription>
              {data.essence && typeof data.essence === 'string' && (
                <Box>
                  <DescriptionHeader>Детальное описание</DescriptionHeader>
                  <Typography>{data.essence}</Typography>
                </Box>
              )}
              {data.hypothesisDigital && typeof data.hypothesisDigital === 'string' && (
                <Box>
                  <DescriptionHeader>Гипотеза прямого влияния</DescriptionHeader>
                  <Typography>{data.hypothesisDigital}</Typography>
                </Box>
              )}
              {data.hypothesisTheory && typeof data.hypothesisTheory === 'string' && (
                <Box>
                  <DescriptionHeader>Гипотеза на подумать</DescriptionHeader>
                  <Typography>{data.hypothesisTheory}</Typography>
                </Box>
              )}
              {data.notes && typeof data.notes === 'string' && (
                <Box>
                  <DescriptionHeader>Примечания</DescriptionHeader>
                  <Typography>{data.notes}</Typography>
                </Box>
              )}
            </FullDescription>
          );
        }

        const handleToggleClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          const rowData = info.row.original as any;
          onToggleDetails?.(rowData.campaignId || null);
          info.row.getToggleExpandedHandler()();
        };

        const isExpanded = info.row.getIsExpanded();

        return (
          <Box>
            <Typography>{String(info.getValue() ?? '')}</Typography>
            <ToggleDetailsButton onClick={handleToggleClick}>
              {isExpanded ? 'Свернуть' : 'Подробно'}
            </ToggleDetailsButton>
          </Box>
        );
      },
    },
    {
      id: 'segmentName',
      title: entityType === 'ad' ? 'Объявление' : 'Сегмент',
      enableFilter: false,
      size: 200,
      sortingFn: 'text',
    },
    ...(['segment', 'group', 'keyword', 'ad'].includes(entityType)
      ? [
          {
            id: 'groupId',
            title: 'ID Группы',
            enableFilter: false as const,
            size: 150,
            sortingFn: 'alphanumeric' as const,
          },
        ]
      : []),
    {
      id: 'campaign',
      title: 'Кампания',
      enableFilter: false,
      children: [
        {
          id: 'campaignId',
          title: 'ID',
          enableFilter: false,
          size: 100,
          sortingFn: 'alphanumeric',
        },
        {
          id: 'campaignName',
          title: 'Название',
          enableFilter: false,
          size: 200,
          sortingFn: 'text',
          cell: info => {
            const row = info.row.original as any;
            const campaignName = info.getValue() as string;
            const {campaignId, accountName} = row;

            const handleFilterClick = (e: React.MouseEvent) => {
              e.stopPropagation();
              onCampaignFilterClick?.(campaignId);
            };

            return (
              <CampaignCellContainer className='campaign-cell-container'>
                <YandexDirectLink
                  campaignId={campaignId}
                  campaignName={campaignName}
                  accountName={accountName}
                  onCampaignNameClick={onCampaignNameClick}
                />
                {onCampaignFilterClick && campaignId && (
                  <FilterIconButton
                    className='filter-icon-button'
                    onClick={handleFilterClick}
                    title='Фильтровать по этой кампании'
                  >
                    <FilterAltOutlinedIcon />
                  </FilterIconButton>
                )}
              </CampaignCellContainer>
            );
          },
        },
      ],
    },
    {
      id: 'kpiFact',
      title: `${kpiName} факт`,
      enableFilter: false,
      size: 200,
      sortingFn: numericSorting,
      cell: info => {
        const isSubRow = info.row.depth > 0;
        if (isSubRow) return null;

        const value = info.getValue() as number | null;
        if (value == null) {
          return <Typography>—</Typography>;
        }

        // Определяем, лучше или хуже цели
        // Для CPL и ДРР: меньше = лучше
        const isPositive = targetKpi > 0 ? value < targetKpi : false;

        // Разница от целевого KPI (не от максимума!)
        const difference = value - targetKpi;

        // Для ДРР рассчитываем progressBarValue по формуле
        let progressBarValue = value;
        if (kpiName === 'ДРР') {
          const rowData = info.row.original as any;
          const justification = rowData.justification;

          if (justification?.budget && justification?.kpi && targetKpi) {
            progressBarValue =
              justification.budget - (justification.budget / justification.kpi) * targetKpi;
          }
        }

        return (
          <KpiAndBudgetCellContainer>
            <Typography>
              {formatNumber(value)} {kpiUnits}
            </Typography>
            {maxKpiFact > 0 && (
              <ProgressBar
                value={progressBarValue}
                maxValue={maxKpiFact}
                isPositive={isPositive}
                kpiName={kpiName}
                difference={difference}
              />
            )}
          </KpiAndBudgetCellContainer>
        );
      },
    },
    {
      id: 'budget',
      title: 'Расход',
      enableFilter: false,
      size: 150,
      sortingFn: numericSorting,
      cell: info => {
        const isSubRow = info.row.depth > 0;
        if (isSubRow) return null;

        const value = info.getValue() as number | null;
        if (value == null) {
          return <Typography>—</Typography>;
        }

        return <Typography>{formatNumber(value)} ₽</Typography>;
      },
    },
    ...(kpiName === 'ДРР'
      ? [
          {
            id: 'revenue',
            title: 'Доход',
            enableFilter: false,
            size: 150,
            sortingFn: numericSorting,
            cell: (info: any) => {
              const isSubRow = info.row.depth > 0;
              if (isSubRow) return null;

              const value = info.getValue() as number | null;
              if (value == null) {
                return <Typography>—</Typography>;
              }

              return <Typography>{formatNumber(value)} ₽</Typography>;
            },
          },
        ]
      : []),
    {
      id: 'recommendation',
      title: 'Рекомендация',
      enableFilter: false,
      size: 200,
      cell: info => {
        const data = info.row.original as any;
        const actionType = data.actionType as ActionType;
        const actionValue = data.actionValue;
        const actionUnits = data.actionUnits;

        if (!actionType) {
          return <Typography>—</Typography>;
        }

        const label = ACTION_TYPE_LABELS[actionType];
        let IconComponent: React.ComponentType<any> | null = null;
        let iconColor: string | undefined;

        // Определяем иконку и цвет
        switch (actionType) {
          case 'LOWER_BID':
            IconComponent = KeyboardDoubleArrowDownIcon;
            iconColor = 'error';
            break;
          case 'INCREASE_BID':
            IconComponent = KeyboardDoubleArrowUpIcon;
            iconColor = 'success';
            break;
          case 'DISABLE':
            IconComponent = CancelIcon;
            iconColor = 'error';
            break;
          case 'MONITOR':
            IconComponent = ScheduleIcon;
        }

        // Формируем текст рекомендации
        let recommendationText = label;
        if (actionValue != null && actionValue !== '') {
          recommendationText += ` ${actionValue}${actionUnits === 'percent' ? '%' : ''}`;
        }

        return (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            {IconComponent && <IconComponent sx={{height: '16px'}} color={iconColor} />}
            <Typography>{recommendationText}</Typography>
          </Box>
        );
      },
    },
    {
      id: 'forecast',
      title: 'Прогноз влияния',
      enableFilter: false,
      children: [
        {
          id: 'forecastKpi',
          title: kpiName,
          enableFilter: false,
          size: 200,
          sortingFn: numericSorting,
          cell: info => {
            const isSubRow = info.row.depth > 0;
            if (isSubRow) return null;

            const value = info.getValue() as number | null;
            if (value == null) {
              return <Typography>—</Typography>;
            }

            // Для CPL и ДРР: отрицательное значение = улучшение = зеленый
            const isPositive = value < 0;

            // Для ДРР используем 1 знак после запятой
            const digitsAfterComma = kpiName === 'ДРР' ? 1 : 0;

            return (
              <KpiAndBudgetCellContainer>
                <Typography>
                  {value > 0 ? '+' : ''}
                  {formatNumber(value, digitsAfterComma)} {kpiUnits}
                </Typography>
                {maxForecastKpi > 0 && (
                  <ProgressBar
                    value={value}
                    maxValue={maxForecastKpi}
                    isPositive={isPositive}
                    kpiName={kpiName}
                  />
                )}
              </KpiAndBudgetCellContainer>
            );
          },
        },
        {
          id: 'forecastBudget',
          title: 'Бюджет',
          enableFilter: false,
          size: 200,
          sortingFn: numericSorting,
          cell: info => {
            const isSubRow = info.row.depth > 0;
            if (isSubRow) return null;

            const value = info.getValue() as number | null;
            if (value == null) {
              return <Typography>—</Typography>;
            }

            // Зеленый если экономия (отрицательное) или движется к целевому бюджету
            const isPositive = value < 0 || (targetBudget > 0 && targetBudget !== plannedBudget);

            return (
              <KpiAndBudgetCellContainer>
                <Typography>
                  {value > 0 ? '+' : ''}
                  {formatNumber(value)} ₽
                </Typography>
                {maxForecastBudget > 0 && (
                  <ProgressBar
                    value={value}
                    maxValue={maxForecastBudget}
                    isPositive={isPositive}
                    kpiName={kpiName}
                  />
                )}
              </KpiAndBudgetCellContainer>
            );
          },
        },
      ],
    },
  ];
};
